import qs from "query-string"
import { Column } from "../components/Table"
import { ContentType, CubeRequestParams, TableOptions } from "../types"
import { getOrgFiltersMap } from "../helpers/filters"
import {
  formatMilliseconds,
  getContentRecordLinkFromUsageContentType,
  getOrgViewNavigationLink
} from "../helpers/tables"
import {
  ANSWERS,
  GOALS,
  GOALS_SCORE_RANGE_BY_SUBJECT,
  GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE,
  GOALS_SCORE_RANGE_BY_TEST,
  GOALS_SCORE_RANGE_BY_TEST_SUBPAGE,
  GOALS_SUMMARY_BY_PROGRAM,
  GOALS_SUMMARY_BY_PROGRAM_SUBPAGE,
  GOALS_SUMMARY_BY_SUBJECT,
  GOALS_SUMMARY_BY_SUBJECT_SUBPAGE,
  QUESTIONS,
  SCORES,
  SCORES_SUBTEST,
  SCORE_GOALS_BY_SUBJECT_VIEW,
  SCORE_GOALS_BY_PROGRAM_VIEW_SUBPAGE,
  SCORE_GOALS_BY_SUBJECT_VIEW_SUBPAGE,
  STUDENT_QUESTIONS,
  SCORE_GOALS_BY_PROGRAM_VIEW
} from "./tables/performance"
import { EventAction, Roles } from "../../mp-utils/generic-utils"
import {
  CONTENT_LIBRARY,
  CONTENT_LIBRARY_ELEMENT,
  CONTENT_LIBRARY_MASTERY_CURRICULUM,
  CONTENT_LIBRARY_MASTERY_LESSON,
  CONTENT_LIBRARY_PRACTICE_TEST,
  CONTENT_LIBRARY_STUDY_HALL
} from "./tables/content"
import { LICENSE_POOLS, LICENSE_ASSIGN } from "./tables/organization"
import { FeatureFlagName } from "../../mp-utils/generic-utils/feature-flags/types"
import { scoresColumnFormatter } from "../helpers"
import { AssignmentStatus } from "../../../types/gqlTypes/graphql"
import {
  formatAssignmentRecordStudentStatus,
  getNameWithNotes
} from "../helpers/tables/assignments"
import { formatDate } from "../../mp-core/helpers/formatters"
import { isUsageActivityLogUserDeleted } from "../helpers/activityLog"

export const HEADER_COLUMNS: Record<string, Record<string, Column[]>> = {
  organization: {
    states: [
      {
        label: "Name",
        name: "org_view_states.code",
        sortable: true,
        size: 250
      },
      {
        label: "Status",
        name: "org_view_states.isActive",
        sortable: true,
        size: 250
      },
      {
        label: "Districts",
        name: "org_view_states.districtCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Schools",
        name: "org_view_states.schoolsCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Active Students",
        name: "org_view_states.activeStudentsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Average Time Spent",
        name: "org_view_states.averageTimeSpent",
        sortable: true,
        type: "duration",
        formatter: formatMilliseconds,
        size: 170,
        align: "right"
      }
    ],
    districts: [
      {
        label: "Name",
        name: "org_view_districts.districtName",
        sortable: true,
        size: 350
      },
      {
        label: "Status",
        name: "org_view_districts.isActive",
        sortable: true,
        size: 100
      },
      {
        label: "District Admins",
        name: "org_view_districts.districtAdminsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Schools",
        name: "org_view_districts.schoolsCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "School Admins",
        name: "org_view_districts.schoolAdminsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Teachers",
        name: "org_view_districts.teachersCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Active Students",
        name: "org_view_districts.activeStudentsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Average Time Spent",
        name: "org_view_districts.averageTimeSpent",
        sortable: true,
        type: "duration",
        formatter: formatMilliseconds,
        size: 170,
        align: "right"
      }
    ],
    schools: [
      {
        label: "Name",
        name: "org_view_schools.name",
        sortable: true,
        size: 350
      },
      {
        label: "Status",
        name: "org_view_schools.isActive",
        sortable: true,
        size: 100
      },
      {
        label: "School Admins",
        name: "org_view_schools.adminsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Teachers",
        name: "org_view_schools.teachersCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Students",
        name: "org_view_schools.studentsCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Active Students",
        name: "org_view_schools.activeStudentsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Average Time Spent",
        name: "org_view_schools.averageTimeSpent",
        sortable: true,
        type: "duration",
        formatter: formatMilliseconds,
        size: 170,
        align: "right"
      }
    ],
    classes: [
      {
        label: "Name",
        name: "org_view_classes.name",
        sortable: true,
        size: 250
      },
      // {
      //   label: "Status",
      //   name: "org_view_classes.isActive",
      //   sortable: true,
      //   size: 100
      // },
      {
        label: "Students",
        name: "org_view_classes.studentsCount",
        sortable: true,
        size: 120,
        align: "right"
      },
      {
        label: "Active Students",
        name: "org_view_classes.activeStudentsCount",
        sortable: true,
        size: 150,
        align: "right"
      },
      {
        label: "Period",
        name: "org_view_classes.period",
        sortable: true,
        type: "number",
        size: 80,
        align: "right"
      },
      {
        label: "Subject",
        name: "org_view_classes.subject",
        sortable: true,
        size: 125
      },
      {
        label: "School",
        name: "org_view_classes.schoolName",
        sortable: true,
        type: "link",
        typeProp: "org_view_classes.schoolId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/schools",
            "org_view_classes.schoolId",
            "org_view_classes.schoolName"
          ),
        linkTooltip: record =>
          `View ${record["org_view_classes.schoolName"]} Record`,
        size: 250
      },
      {
        label: "District",
        name: "org_view_classes.districtName",
        sortable: true,
        type: "link",
        typeProp: "org_view_classes.districtId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/districts",
            "org_view_classes.districtId",
            "org_view_classes.districtName"
          ),
        linkTooltip: record =>
          `View ${record["org_view_classes.districtName"]} Record`,
        size: 250
      },
      {
        label: "State",
        name: "org_view_classes.state",
        sortable: true,
        type: "link",
        typeProp: "org_view_classes.state",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/states",
            "org_view_classes.state"
          ),
        linkTooltip: record =>
          `View ${record["org_view_classes.state"]} Record`,
        size: 100
      },
      {
        label: "MasteryPrep Course",
        type: "course",
        name: "org_view_classes.eocSubjects",
        sortable: true,
        size: 175
      },
      {
        label: "Teacher",
        name: "org_view_classes.teacherName",
        sortable: true,
        mask: true,
        type: "link",
        typeProp: "org_view_classes.teacherId",
        linkTooltip: record =>
          `View ${record["org_view_classes.teacherName"]} Record`,
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/teachers",
            "org_view_classes.teacherId",
            "org_view_classes.teacherName"
          ),
        size: 250
      },
      {
        label: "Average Time Spent",
        name: "org_view_classes.averageTimeSpent",
        sortable: true,
        type: "duration",
        formatter: formatMilliseconds,
        size: 170,
        align: "right"
      }
    ],
    admins: [
      {
        label: "Name",
        name: "org_view_admins.fullName",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "Role",
        name: "org_view_admins.role",
        sortable: true,
        size: 150
      },
      {
        label: "School",
        name: "org_view_admins.schoolName",
        sortable: true,
        type: "link",
        typeProp: "org_view_admins.schoolId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/schools",
            "org_view_admins.schoolId",
            "org_view_admins.schoolName"
          ),
        linkTooltip: record =>
          `View ${record["org_view_admins.schoolName"]} Record`,
        size: 250
      },
      {
        label: "Status",
        name: "org_view_admins.isActive",
        sortable: true,
        size: 100
      },
      {
        label: "Last Active",
        name: "org_view_admins.lastActive",
        type: "dateTime",
        sortable: true,
        size: 200
      },
      {
        label: "State",
        name: "org_view_admins.state",
        sortable: true,
        type: "link",
        typeProp: "org_view_admins.state",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/states",
            "org_view_admins.state"
          ),
        linkTooltip: record => `View ${record["org_view_admins.state"]} Record`,
        size: 100
      },
      {
        label: "Email",
        name: "org_view_admins.email",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "District",
        name: "org_view_admins.districtName",
        sortable: true,
        type: "link",
        typeProp: "org_view_admins.districtId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/districts",
            "org_view_admins.districtId",
            "org_view_admins.districtName"
          ),
        linkTooltip: record =>
          `View ${record["org_view_admins.districtName"]} Record`,
        size: 250
      }
    ],
    teachers: [
      {
        label: "Name",
        name: "org_view_teachers.fullName",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "School",
        name: "org_view_teachers.schoolName",
        sortable: true,
        type: "link",
        typeProp: "org_view_teachers.schoolId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/schools",
            "org_view_teachers.schoolId",
            "org_view_teachers.schoolName"
          ),
        linkTooltip: record =>
          `View ${record["org_view_teachers.schoolName"]} Record`,
        size: 250
      },
      {
        label: "Classes",
        name: "org_view_teachers.classesCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Status",
        name: "org_view_teachers.isActive",
        sortable: true,
        size: 100
      },
      {
        label: "Last Active",
        name: "org_view_teachers.teacherLastActive",
        type: "dateTime",
        sortable: true,
        size: 200
      },
      {
        label: "District",
        name: "org_view_teachers.districtName",
        sortable: true,
        type: "link",
        typeProp: "org_view_teachers.districtId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/districts",
            "org_view_teachers.districtId",
            "org_view_teachers.districtName"
          ),
        linkTooltip: record =>
          `View ${record["org_view_teachers.districtName"]} Record`,
        size: 250
      },
      {
        label: "State",
        name: "org_view_teachers.state",
        sortable: true,
        type: "link",
        typeProp: "org_view_teachers.state",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/states",
            "org_view_teachers.state"
          ),
        linkTooltip: record =>
          `View ${record["org_view_teachers.state"]} Record`,
        size: 100
      },
      {
        label: "Email",
        name: "org_view_teachers.email",
        sortable: true,
        size: 250,
        mask: true
      }
    ],
    students: [
      {
        label: "Name",
        name: "org_view_students.studentFullname",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "Classes",
        name: "org_view_students.classesCount",
        sortable: true,
        size: 100,
        align: "right",
        linkTooltip: record =>
          `View ${record["org_view_students.schoolName"]} Record`
      },
      {
        label: "Status",
        name: "org_view_students.isActive",
        sortable: true,
        size: 100
      },
      {
        label: "First Name",
        name: "org_view_students.studentFirstName",
        sortable: true,
        size: 175
      },
      {
        label: "Last Name",
        name: "org_view_students.studentLastName",
        sortable: true,
        size: 175
      },
      {
        label: "School",
        name: "org_view_students.schoolName",
        sortable: true,
        type: "link",
        typeProp: "org_view_students.schoolId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/schools",
            "org_view_students.schoolId",
            "org_view_students.schoolName"
          ),
        size: 250,
        linkTooltip: record =>
          `View ${record["org_view_students.schoolName"]} Record`
      },
      {
        label: "District",
        name: "org_view_students.districtName",
        sortable: true,
        type: "link",
        typeProp: "org_view_students.districtId",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/districts",
            "org_view_students.districtId",
            "org_view_students.districtName"
          ),
        size: 250,
        linkTooltip: record =>
          `View ${record["org_view_students.districtName"]} Record`
      },
      {
        label: "State",
        name: "org_view_students.state",
        sortable: true,
        type: "link",
        typeProp: "org_view_students.state",
        linkUrl: record =>
          getOrgViewNavigationLink(
            record,
            "/reporting/organization/states",
            "org_view_students.state"
          ),
        size: 100,
        linkTooltip: record =>
          `View ${record["org_view_students.state"]} Record`
      },
      {
        label: "Email",
        name: "org_view_students.email",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "Student ID",
        name: "org_view_students.externalStudentId",
        sortable: true,
        mask: true,
        size: 150
      },
      {
        label: "Grade",
        name: "org_view_students.grade",
        sortable: false,
        size: 80
      },
      {
        label: "Username",
        name: "org_view_students.studentUserName",
        sortable: true,
        size: 250,
        mask: true
      } //,
      // {
      //   label: "Time Spent",
      //   name: "org_view_students.timeSpent",
      //   sortable: true,
      //   formatter: formatMilliseconds,
      //   type: "duration",
      //   size: 150,
      //   align: "right"
      // }
    ],
    "license-pools": [
      {
        label: "License Pool Type",
        name: "org_view_licenses.orgType",
        sortable: true,
        size: 200,
        formatter: (record, _prop) => {
          const orgType = record["org_view_licenses.orgType"]
          if (orgType === "SCHOOL") {
            const schoolName =
              record["org_view_licenses.schoolName"] || "Unknown School"
            return `School: ${schoolName}`
          }
          const districtName =
            record["org_view_licenses.districtName"] || "Unknown District"
          return `District: ${districtName}`
        }
      },
      {
        label: "Max License Pool Size",
        name: "org_view_licenses.maxLicenses",
        sortable: true,
        size: 150
      },
      {
        label: "Start Date (UTC)",
        name: "org_view_licenses.startDate",
        sortable: true,
        size: 150,
        type: "utcdate"
      },
      {
        label: "End Date (UTC)",
        name: "org_view_licenses.endDate",
        sortable: true,
        size: 150,
        type: "utcdate"
      }
    ],
    "license-assign": [
      {
        label: "Name",
        name: "org_view_students.studentFullname",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "License Status",
        name: "org_view_students.hasActiveLicense",
        sortable: true,
        size: 200,
        type: "chip",
        typeProp: () => "primary",
        variant: record =>
          record["org_view_students.hasActiveLicense"] === "Licensed"
            ? "filled"
            : "light",
        formatter: record => {
          if (record["org_view_students.hasActiveLicense"] !== "Licensed") {
            return "Unlicensed"
          }

          // Check for license type details
          const licenseOrgType = record["org_view_students.licenseOrgType"]
          const licenseOrgName = record["org_view_students.licenseOrgName"]

          // Use unified approach with licenseOrgName
          if (licenseOrgType && licenseOrgName) {
            const prefix = licenseOrgType === "SCHOOL" ? "School" : "District"
            return `${prefix}: ${licenseOrgName}`
          }

          // Fallback to simple "Licensed" if type details not available
          return "Licensed"
        }
      },
      {
        label: "Grade",
        name: "org_view_students.grade",
        sortable: false,
        size: 80
      },
      {
        label: "Classes",
        name: "org_view_students.classesCount",
        sortable: true,
        size: 100,
        align: "right"
      },
      {
        label: "Email",
        name: "org_view_students.email",
        sortable: true,
        size: 250,
        mask: true
      },
      {
        label: "Username",
        name: "org_view_students.studentUserName",
        sortable: true,
        size: 200,
        mask: true
      }
    ]
  },
  content: {
    "content-library": CONTENT_LIBRARY.columns,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.columns,
    "content-library/study-hall": CONTENT_LIBRARY_STUDY_HALL.columns,
    "content-library/practice-test": CONTENT_LIBRARY_PRACTICE_TEST.columns,
    "content-library/mastery-lesson": CONTENT_LIBRARY_MASTERY_LESSON.columns,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.columns
  },
  insights: {
    "activity-log": [
      {
        label: "Date/Time",
        name: "usage_activity_logs.dateTime",
        sortable: true,
        type: "dateTime"
      },
      {
        label: "Name",
        name: "usage_activity_logs.name",
        sortable: true,
        mask: true,
        type: "link",
        typeProp: "usage_activity_logs.userId",
        linkUrl: record =>
          !isUsageActivityLogUserDeleted(record)
            ? `/reporting/organization/${
                record["usage_activity_logs.userRole"] === Roles.Student
                  ? "students"
                  : "teachers"
              }`
            : null,
        linkTooltip: record =>
          !isUsageActivityLogUserDeleted(record)
            ? `View ${record["usage_activity_logs.name"]} Record`
            : null
      },
      {
        label: "Student ID",
        name: "usage_activity_logs.externalStudentId",
        sortable: true,
        mask: true,
        size: 150
      },
      {
        label: "Activity",
        name: "usage_activity_logs.activityType",
        sortable: true,
        type: "activity"
      },
      {
        label: "Content Type",
        name: "usage_activity_logs.contentType",
        sortable: true,
        type: "usageContentType"
      },
      {
        label: "Content",
        name: "usage_activity_logs.contentName",
        sortable: true,
        type: "link",
        typeProp: "usage_activity_logs.contentDisplayId",
        linkUrl: record =>
          getContentRecordLinkFromUsageContentType(
            record["usage_activity_logs.contentType"],
            record["usage_activity_logs.contentDisplayId"]
          ),
        linkTooltip: "View Content Record",
        applySearchParams: true
      },
      {
        label: "Test",
        name: "usage_activity_logs.test",
        sortable: true
      },
      /*
      {
        label: "Attempt",
        name: "usage_activity_logs.attempt",
        sortable: true,
        type: "number",
        align: "right",
        formatter: (record, prop) => {
          // NOTE: replace value to "--" if not Complete
          const activityType = record["usage_activity_logs.activityType"]

          if (activityType !== EventAction.FINISH) {
            return "--"
          }

          return record[prop]
        }
      },
      */
      {
        label: "Time Spent",
        name: "usage_activity_logs.timeSpent",
        sortable: true,
        formatter: (record, prop) => {
          // NOTE: replace value to "--" if not Complete
          const activityType = record["usage_activity_logs.activityType"]

          if (activityType !== EventAction.FINISH) {
            return "--"
          }

          return formatMilliseconds(record, prop)
        },
        type: "duration",
        columnContentTypes: [
          "default",
          "TEST",
          "DIAGNOSTIC_TEST",
          "ML_LESSON",
          "ML_PRACTICE_SET"
        ],
        align: "right"
      },
      {
        label: "Score",
        name: "usage_activity_logs.score",
        sortable: true,
        type: "number",
        align: "right",
        columnContentTypes: ["default", "TEST", "DIAGNOSTIC_TEST"],
        formatter: (record, prop, context) => {
          return scoresColumnFormatter(
            record["usage_activity_logs.program"],
            record["usage_activity_logs.subject"],
            record["usage_activity_logs.contentType"] === "DIAGNOSTIC_TEST",
            record[prop],
            context,
            record["usage_activity_logs.activityType"]
          )
        }
        // TODO: add tooltip when column will be a link
        // linkTooltip: "View Performance"
      },
      {
        label: "Accuracy",
        name: "usage_activity_logs.score",
        sortable: true,
        columnContentTypes: [
          "ML_LESSON",
          "ML_PRACTICE_SET",
          "SH_VIDEO",
          "SH_PRACTICE_SET",
          "MC_ENTRY_TICKET",
          "MC_EXIT_TICKET"
        ]
        // TODO: add tooltip when column will be a link
        // linkTooltip: "View Performance"
      }
    ],
    "details-usage": [
      {
        label: "Date/Time",
        name: "usage_activity_logs.dateTime",
        sortable: true,
        type: "dateTime"
      },
      {
        label: "Student ID",
        name: "usage_activity_logs.externalStudentId",
        sortable: true,
        mask: true,
        size: 150
      },
      {
        label: "Activity",
        name: "usage_activity_logs.activityType",
        sortable: true,
        type: "activity"
      },
      {
        label: "Content Type",
        name: "usage_activity_logs.contentType",
        sortable: true,
        type: "usageContentType"
      },
      {
        label: "Content",
        name: "usage_activity_logs.contentName",
        sortable: true,
        type: "link",
        typeProp: "usage_activity_logs.contentDisplayId",
        linkUrl: record =>
          getContentRecordLinkFromUsageContentType(
            record["usage_activity_logs.contentType"],
            record["usage_activity_logs.contentDisplayId"]
          ),
        linkTooltip: "View Content Record",
        applySearchParams: true
      },
      {
        label: "Test",
        name: "usage_activity_logs.test",
        sortable: true
      },
      {
        label: "Time Spent",
        name: "usage_activity_logs.timeSpent",
        sortable: true,
        formatter: (record, prop) => {
          // NOTE: replace value to "--" if not Complete
          const activityType = record["usage_activity_logs.activityType"]

          if (activityType !== EventAction.FINISH) {
            return "--"
          }

          return formatMilliseconds(record, prop)
        },
        type: "duration",
        columnContentTypes: [
          "default",
          "TEST",
          "DIAGNOSTIC_TEST",
          "ML_LESSON",
          "ML_PRACTICE_SET"
        ],
        align: "right"
      },
      {
        label: "Score",
        name: "usage_activity_logs.score",
        sortable: true,
        type: "number",
        align: "right",
        columnContentTypes: ["default", "TEST", "DIAGNOSTIC_TEST"],
        formatter: (record, prop, context) => {
          return scoresColumnFormatter(
            record["usage_activity_logs.program"],
            record["usage_activity_logs.subject"],
            record["usage_activity_logs.contentType"] === "DIAGNOSTIC_TEST",
            record[prop],
            context,
            record["usage_activity_logs.activityType"]
          )
        }
      },
      {
        label: "Accuracy",
        name: "usage_activity_logs.score",
        sortable: true,
        columnContentTypes: [
          "ML_LESSON",
          "ML_PRACTICE_SET",
          "SH_VIDEO",
          "SH_PRACTICE_SET",
          "MC_ENTRY_TICKET",
          "MC_EXIT_TICKET"
        ]
      }
    ],
    "students-usage": [
      {
        label: "Name",
        name: "usage_students.studentName",
        sortable: true,
        mask: true,
        type: "link",
        typeProp: record =>
          record["usage_students.studentId"] ?? record["studentId"],
        linkUrl: "/reporting/organization/students",
        linkTooltip: record =>
          `View ${record["usage_students.studentName"]} Record`
      },
      {
        label: "Student ID",
        name: "usage_students.externalStudentId",
        sortable: true,
        mask: true,
        size: 150
      },
      { label: "Grade", name: "usage_students.grade", sortable: false },
      {
        label: "Tests Completed",
        name: "usage_students.submittedTests",
        sortable: true,
        type: "link",
        linkUrl: record =>
          `/reporting/insights/usage/activity-log?${qs.stringify({
            usageContentType: ["TEST", "DIAGNOSTIC_TEST"],
            activity: EventAction.FINISH,
            student: record["usage_students.studentId"]
          })}`,
        linkTooltip: record =>
          `View ${record["usage_students.studentName"]}'s Tests Completed`,
        align: "right"
      },
      {
        label: "Sections Completed",
        name: "usage_students.subtestsCompleted",
        sortable: true,
        type: "link",
        linkUrl: record =>
          `/reporting/insights/usage/activity-log?${qs.stringify({
            usageContentType: ["TEST", "DIAGNOSTIC_TEST"],
            activity: EventAction.FINISH,
            student: record["usage_students.studentId"]
          })}`,
        linkTooltip: record =>
          `View ${record["usage_students.studentName"]}'s Sections Completed`,
        align: "right"
      },
      {
        label: "Questions Answered",
        name: "usage_students.questionsAnswered",
        sortable: true,
        type: "link",
        linkUrl: record =>
          `/reporting/insights/usage/activity-log?${qs.stringify({
            activity: EventAction.ANSWER,
            student: record["usage_students.studentId"]
          })}`,
        linkTooltip: record =>
          `View ${record["usage_students.studentName"]}'s Questions Answered`,
        align: "right"
      },
      {
        label: "Lessons Completed",
        name: "usage_students.lessonsCompleted",
        sortable: true,
        type: "link",
        linkUrl: record =>
          `/reporting/insights/usage/activity-log?${qs.stringify({
            usageContentType: [
              "ML_LESSON",
              "ML_PRACTICE_SET",
              "SH_PRACTICE_SET",
              "MC_EXIT_TICKET",
              "MC_ENTRY_TICKET"
            ],
            activity: EventAction.FINISH,
            student: record["usage_students.studentId"]
          })}`,
        linkTooltip: record =>
          `View ${record["usage_students.studentName"]}'s Lessons Completed`,
        align: "right"
      },
      {
        label: "Page Views",
        name: "usage_students.pageViews",
        sortable: true,
        type: "number",
        align: "right"
      },
      {
        label: "Time Spent",
        name: "usage_students.timeSpent",
        sortable: true,
        formatter: formatMilliseconds,
        type: "duration",
        align: "right"
      },
      {
        label: "Logins",
        name: "usage_students.logins",
        sortable: true,
        type: "number",
        align: "right"
      }
    ],
    "teachers-usage": [
      {
        label: "Name",
        name: "usage_teachers.teacherName",
        sortable: true,
        mask: true,
        type: "link",
        typeProp: "usage_teachers.teacherId",
        linkUrl: "/reporting/organization/teachers",
        linkTooltip: record =>
          `View ${record["usage_teachers.teacherName"]} Record`
      },
      // {
      //   label: "Time Spent",
      //   name: "usage_teachers.timeSpent",
      //   sortable: true,
      //   formatter: formatMilliseconds,
      //   type: "duration",
      //   align: "right"
      // },
      {
        label: "Page Views",
        name: "usage_teachers.pageViews",
        sortable: true,
        type: "number",
        align: "right"
      },
      {
        label: "Logins",
        name: "usage_teachers.logins",
        sortable: true,
        type: "number",
        align: "right"
      }
    ],
    scores: SCORES.columns,
    "subtests-scores": SCORES_SUBTEST.columns,
    questions: QUESTIONS.columns,
    studentQuestions: STUDENT_QUESTIONS.columns,
    answers: ANSWERS.columns,
    goals: GOALS.columns,
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]: GOALS_SCORE_RANGE_BY_TEST.columns,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.columns,
    "custom-reports": [
      { label: "Name", name: "name", sortable: true },
      { label: "Submitted", name: "updatedAt", sortable: true },
      { label: "Last Generated", name: "lastGeneratedAt", sortable: true },
      {
        label: "Status",
        name: "status",
        type: "chip",
        variant: () => "light",
        typeProp: record => {
          if (record?.status?.toLowerCase() === "available") return "success"
          if (record?.status?.toLowerCase() === "generating") return "warning"
          if (record?.status?.toLowerCase() === "failed") return "error"
          return "default"
        }
      },
      { label: "Schools", name: "schoolsShown", sortable: false },
      { label: "Test", name: "test", sortable: true },
      { label: "Date", name: "dateRange", sortable: false }
    ],
    "performance-report": [
      {
        label: "Name",
        name: "name",
        sortable: true
      },
      {
        label: "Test",
        name: "programDisplayName",
        sortableName: "program",
        sortable: true
      },
      {
        label: "Last Activity",
        name: "lastActivity",
        sortable: true,
        formatter: (record, prop) => {
          return formatDate(record[prop], true, true)
        }
      }
    ]
  },
  "teaching-tools": {
    assignments: [
      {
        label: "Name",
        name: "assignments.name",
        sortable: true,
        size: 250,
        formatter: record =>
          getNameWithNotes(
            record["assignments.name"],
            record["assignments.data"]?.settings?.notes
          )
      },
      {
        label: "Assignee",
        name: "assignments.assignee",
        sortable: false,
        type: "multipleItems",
        size: 200
      },
      {
        label: "Assignment Status",
        name: "assignments.assignmentStatus",
        sortable: false,
        size: 150,
        type: "chip",
        typeProp: record => {
          const status: AssignmentStatus =
            record["assignments.assignmentStatus"]
          if (status === "OPEN") return "success"
          if (status === "PAST_DUE") return "error"
          if (status === "SCHEDULED") return "primary"
          return "default"
        },
        variant: () => "light",
        formatter: record => {
          const value = record["assignments.assignmentStatus"]
          if (!value) return null

          return value
            .split("_")
            .map(
              word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ")
        }
      },
      {
        label: "Due Date",
        name: "assignments.dueDate",
        sortable: true,
        type: "localDate",
        size: 120
      },
      {
        label: "Open Date",
        name: "assignments.openDate",
        sortable: true,
        type: "localDate",
        size: 120
      },
      {
        label: "Allow Late",
        name: "assignments.allowLate",
        sortable: false,
        size: 120,
        formatter: record => {
          return record["assignments.allowLate"] ? "Yes" : "No"
        }
      },
      {
        label: "Close Date",
        name: "assignments.closeDate",
        sortable: true,
        type: "localDate",
        size: 120
      },
      {
        label: "% Complete",
        name: "assignments.completedPercent",
        sortable: false,
        type: "percent",
        size: 100,
        align: "right",
        formatter: (record, prop) => {
          const score = record[prop]
          if (score === null || score === undefined) return "--"
          return `${Math.round(score)}%`
        },
        filter: filters => !filters.student || filters.student.length === 0
      },
      // {
      //   label: "Status",
      //   name: "assignments.status",
      //   sortable: true,
      //   size: 150,
      //   type: "chip",
      //   typeProp: record => {
      //     const status = record["assignments.status"]
      //     if (status === "Completed") return "success"
      //     if (status === "In Progress") return "primary"
      //     if (status === "Not Started") return "default"
      //     return "default"
      //   },
      //   variant: () => "light",
      //   formatter: record => {
      //     return record["assignments.status"]
      //   },
      //   filter: filters => !!filters.student && filters.student.length > 0
      // },
      {
        label: "Average Score",
        name: "assignments.averageScore",
        sortable: false,
        type: "percent",
        size: 100,
        align: "right",
        formatter: (record, prop) => {
          const score = record[prop]
          if (score === null || score === undefined) return "--"
          return `${score}%`
        },
        filter: filters => !filters.student || filters.student.length === 0
      }
      // {
      //   label: "Score",
      //   name: "assignments.score",
      //   sortable: true,
      //   type: "percent",
      //   size: 100,
      //   align: "right",
      //   formatter: (record, prop) => {
      //     const score = record[prop]
      //     if (score === null || score === undefined) return "--"
      //     return `${score}%`
      //   },
      //   filter: filters => !!filters.student && filters.student.length > 0
      // }
    ],
    "assignment-record-students": [
      {
        label: "Student",
        name: "assignment_record_students.studentName",
        sortable: true,
        size: 240,
        mask: true
      },
      {
        label: "Class",
        name: "assignment_record_students.classNames",
        sortable: false,
        type: "multipleItems",
        size: 200
      },
      {
        label: "Status",
        name: "assignment_record_students.status",
        sortable: true,
        size: 150,
        type: "chip",
        typeProp: record => {
          const status = record["assignment_record_students.status"]
          if (status === "COMPLETED") return "success"
          if (status === "STARTED") return "primary"
          return "default"
        },
        variant: () => "light",
        formatter: record => {
          return formatAssignmentRecordStudentStatus(
            record["assignment_record_students.status"]
          )
        }
      },
      {
        label: "Completed Date",
        name: "assignment_record_students.completedAt",
        sortable: true,
        type: "localDate",
        size: 150
      },
      {
        label: "% Complete",
        name: "assignment_record_students.percentComplete",
        sortable: true,
        type: "percent",
        size: 120,
        align: "right",
        formatter: (record, prop) => {
          const value = record[prop]
          if (value === null || value === undefined) return "--"
          return `${Math.round(value)}%`
        }
      },
      {
        label: "Score",
        name: "assignment_record_students.accuracy",
        sortable: true,
        type: "percent",
        size: 120,
        align: "right",
        formatter: (record, prop) => {
          const value = record[prop]
          // Do not show a score unless the assingment is fully completed
          if (value === null || value === undefined || record["assignment_record_students.status"] !== "COMPLETED") return "--"
          return `${Math.round(value)}%`
        }
      }
    ]
  },
  "assignments-drawer": {
    content: [
      {
        label: "Name",
        name: "content_view_ml_lessons.name",
        sortable: true
      }
    ],
    students: [
      {
        label: "Name",
        name: "org_view_students.studentFullname",
        sortable: true,
        mask: true
      }
    ],
    classes: [
      {
        label: "Name",
        name: "org_view_classes.name",
        sortable: true
      }
    ]
    // segments: [
    //   {
    //     label: "Name",
    //     name: "org_view_segments.name",
    //     sortable: true
    //   }
    // ]
  }
}

export const DEFAULT_DISPLAYED_PROPERTIES: Record<
  string,
  Record<string, string[]>
> = {
  organization: {
    states: [
      "org_view_states.code",
      "org_view_states.isActive",
      "org_view_states.districtCount",
      "org_view_states.schoolsCount",
      "org_view_states.activeStudentsCount"
    ],
    districts: [
      "org_view_districts.districtName",
      "org_view_districts.isActive",
      "org_view_districts.districtAdminsCount",
      "org_view_districts.schoolsCount",
      "org_view_districts.schoolAdminsCount",
      "org_view_districts.teachersCount",
      "org_view_districts.activeStudentsCount",
      "org_view_districts.averageTimeSpent"
    ],
    schools: [
      "org_view_schools.name",
      "org_view_schools.isActive",
      "org_view_schools.adminsCount",
      "org_view_schools.teachersCount",
      "org_view_schools.activeStudentsCount",
      "org_view_schools.averageTimeSpent"
    ],
    classes: [
      "org_view_classes.name",
      "org_view_classes.teacherName",
      "org_view_classes.schoolName",
      // "org_view_classes.isActive",
      "org_view_classes.eocSubjects",
      "org_view_classes.studentsCount",
      "org_view_classes.activeStudentsCount",
      "org_view_classes.averageTimeSpent"
    ],
    admins: [
      "org_view_admins.fullName",
      "org_view_admins.role",
      "org_view_admins.schoolName",
      "org_view_admins.isActive",
      "org_view_admins.lastActive",
      "org_view_admins.email"
    ],
    teachers: [
      "org_view_teachers.fullName",
      "org_view_teachers.schoolName",
      "org_view_teachers.classesCount",
      "org_view_teachers.isActive",
      "org_view_teachers.teacherLastActive",
      "org_view_teachers.email"
    ],
    students: [
      "org_view_students.studentFullname",
      "org_view_students.classesCount",
      "org_view_students.isActive",
      //"org_view_students.timeSpent",  // commenting out for performance
      "org_view_students.email",
      "org_view_students.studentUserName"
    ],
    "license-pools": LICENSE_POOLS.displayedProperties,
    "license-assign": LICENSE_ASSIGN.displayedProperties
  },
  content: {
    "content-library": CONTENT_LIBRARY.displayedProperties,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.displayedProperties,
    "content-library/study-hall":
      CONTENT_LIBRARY_STUDY_HALL.displayedProperties,
    "content-library/practice-test":
      CONTENT_LIBRARY_PRACTICE_TEST.displayedProperties,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.displayedProperties,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.displayedProperties
  },
  insights: {
    "activity-log": [
      "usage_activity_logs.name",
      "usage_activity_logs.dateTime",
      "usage_activity_logs.activityType",
      "usage_activity_logs.contentType",
      "usage_activity_logs.contentName",
      "usage_activity_logs.test",
      "usage_activity_logs.score",
      "usage_activity_logs.userId",
      "usage_activity_logs.contentId",
      "usage_activity_logs.timeSpent",
      //"usage_activity_logs.attempt",
      "usage_activity_logs.isUserDeleted",
      "usage_activity_logs.teacherName",
      "usage_activity_logs.studentName",
      "usage_activity_logs.program",
      "usage_activity_logs.subject",
      "usage_activity_logs.userRole"
    ],
    "details-usage": [
      "usage_activity_logs.dateTime",
      "usage_activity_logs.name",
      "usage_activity_logs.activityType",
      "usage_activity_logs.contentName",
      "usage_activity_logs.contentType",
      "usage_activity_logs.test",
      "usage_activity_logs.timeSpent",
      "usage_activity_logs.score",
      "usage_activity_logs.program",
      "usage_activity_logs.subject"
    ],
    "students-usage": [
      "usage_students.studentName",
      "usage_students.timeSpent",
      "usage_students.pageViews",
      "usage_students.submittedTests",
      "usage_students.subtestsCompleted",
      "usage_students.lessonsCompleted",
      "usage_students.questionsAnswered",
      "usage_students.logins"
    ],
    "teachers-usage": [
      "usage_teachers.teacherName",
      "usage_teachers.logins",
      "usage_teachers.pageViews"
      // "usage_teachers.timeSpent"
    ],
    scores: SCORES.displayedProperties,
    "subtests-scores": SCORES_SUBTEST.displayedProperties,
    questions: QUESTIONS.displayedProperties,
    studentQuestions: STUDENT_QUESTIONS.displayedProperties,
    answers: ANSWERS.displayedProperties,
    goals: GOALS.displayedProperties,
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_TEST.displayedProperties,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.displayedProperties,
    "custom-reports": [
      "name",
      "updatedAt",
      "lastGeneratedAt",
      "status",
      "schoolsShown",
      "test",
      "dateRange"
    ],
    "performance-report": ["id", "name", "program", "lastActivity"]
  },
  "teaching-tools": {
    assignments: [
      "assignments.name",
      "assignments.assignee",
      "assignments.assignmentStatus",
      "assignments.dueDate",
      "assignments.openDate",
      "assignments.allowLate",
      "assignments.closeDate",
      "assignments.completedPercent",
      "assignments.status",
      "assignments.averageScore",
      "assignments.score"
    ],
    "assignment-record-students": [
      "assignment_record_students.studentName",
      "assignment_record_students.classNames",
      "assignment_record_students.status",
      "assignment_record_students.completedAt",
      "assignment_record_students.percentComplete",
      "assignment_record_students.accuracy"
    ]
  }
}

export const REQUEST_PARAMS: Record<
  string,
  Record<string, CubeRequestParams>
> = {
  organization: {
    states: {
      dimensions: [
        "org_view_states.code",
        "org_view_states.isActive",
        "org_view_states.activeStudentsCount",
        "org_view_states.averageTimeSpent"
      ],
      measures: [
        "org_view_states.districtCount",
        "org_view_states.schoolsCount"
      ]
    },
    districts: {
      dimensions: [
        "org_view_districts.districtId",
        "org_view_districts.districtName",
        "org_view_districts.isActive",
        "org_view_districts.averageTimeSpent",
        "org_view_districts.activeStudentsCount"
      ],
      measures: [
        "org_view_districts.districtAdminsCount",
        "org_view_districts.schoolAdminsCount",
        "org_view_districts.schoolsCount",
        "org_view_districts.teachersCount"
      ]
    },
    schools: {
      dimensions: [
        "org_view_schools.id",
        "org_view_schools.name",
        "org_view_schools.districtId",
        "org_view_schools.averageTimeSpent",
        "org_view_schools.isActive",
        "org_view_schools.activeStudentsCount"
      ],
      measures: [
        "org_view_schools.adminsCount",
        "org_view_schools.studentsCount",
        "org_view_schools.teachersCount"
      ]
    },
    classes: {
      dimensions: [
        "org_view_classes.id",
        "org_view_classes.name",
        "org_view_classes.period",
        "org_view_classes.subject",
        "org_view_classes.eocSubjects",
        "org_view_classes.schoolId",
        "org_view_classes.schoolName",
        "org_view_classes.districtId",
        "org_view_classes.districtName",
        "org_view_classes.state",
        "org_view_classes.teacherId",
        "org_view_classes.teacherName"
      ],
      measures: [
        "org_view_classes.activeStudentsCount",
        "org_view_classes.averageTimeSpent",
        // "org_view_classes.isActive",
        "org_view_classes.studentsCount"
      ]
    },
    classesByStudent: {
      dimensions: ["org_view_classes.id"],
      measures: [
        "org_view_classes.studentsCount",
        "org_view_classes.averageTimeSpent",
        "org_view_classes.activeStudentsCount"
      ]
    },
    admins: {
      dimensions: [
        "org_view_admins.id",
        "org_view_admins.fullName",
        "org_view_admins.role",
        "org_view_admins.email",
        "org_view_admins.schoolId",
        "org_view_admins.schoolName",
        "org_view_admins.districtId",
        "org_view_admins.districtName",
        "org_view_admins.state"
      ],
      measures: ["org_view_admins.isActive", "org_view_admins.lastActive"]
    },
    teachers: {
      dimensions: [
        "org_view_teachers.id",
        "org_view_teachers.fullName",
        "org_view_teachers.schoolId",
        "org_view_teachers.schoolName",
        "org_view_teachers.email",
        "org_view_teachers.state",
        "org_view_teachers.districtId",
        "org_view_teachers.districtName",
        "org_view_teachers.teacherLastActive"
      ],
      measures: ["org_view_teachers.classesCount", "org_view_teachers.isActive"]
    },
    students: {
      dimensions: [
        "org_view_students.studentId",
        "org_view_students.studentFirstName",
        "org_view_students.studentFullname",
        "org_view_students.studentLastName",
        "org_view_students.studentUserName",
        "org_view_students.state",
        "org_view_students.schoolName",
        "org_view_students.schoolId",
        "org_view_students.grade",
        "org_view_students.email",
        "org_view_students.districtName",
        "org_view_students.districtId",
        "org_view_students.isActive",
        "org_view_students.timeSpent",
        "org_view_students.externalStudentId"
      ],
      measures: ["org_view_students.classesCount"]
    },
    "license-pools": LICENSE_POOLS.requestParams,
    "license-assign": LICENSE_ASSIGN.requestParams
  },
  content: {
    "content-library": CONTENT_LIBRARY.requestParams,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.requestParams,
    "content-library/study-hall": CONTENT_LIBRARY_STUDY_HALL.requestParams,
    "content-library/practice-test":
      CONTENT_LIBRARY_PRACTICE_TEST.requestParams,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.requestParams,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.requestParams
  },
  insights: {
    "activity-log": {
      dimensions: [
        "usage_activity_logs.dateTime",
        "usage_activity_logs.activityType",
        "usage_activity_logs.contentType",
        "usage_activity_logs.contentName",
        "usage_activity_logs.score",
        "usage_activity_logs.userId",
        "usage_activity_logs.contentId",
        "usage_activity_logs.contentDisplayId",
        "usage_activity_logs.timeSpent",
        //"usage_activity_logs.attempt",
        "usage_activity_logs.isUserDeleted",
        "usage_activity_logs.teacherName",
        "usage_activity_logs.studentName",
        "usage_activity_logs.program",
        "usage_activity_logs.subject",
        "usage_activity_logs.userRole"
      ],
      measures: [
        "usage_activity_logs.name",
        "usage_activity_logs.test",
        "usage_activity_logs.externalStudentId"
      ],
      filters: [
        {
          member: "usage_activity_logs.activityType",
          operator: "notEquals",
          values: ["view"]
        }
      ]
    },
    "student-activity-log": {
      dimensions: [
        "usage_activity_logs.dateTime",
        "usage_activity_logs.activityType",
        "usage_activity_logs.contentType",
        "usage_activity_logs.contentName"
      ],
      filters: [
        {
          member: "usage_activity_logs.activityType",
          operator: "equals",
          values: ["view"]
        }
        // {
        //   member: "usage_activity_logs.contentType",
        //   operator: "equals",
        //   values: ["ebook"]
        // }
      ]
    },
    "details-usage": {
      dimensions: [
        "usage_activity_logs.dateTime",
        "usage_activity_logs.activityType",
        "usage_activity_logs.contentType",
        //"usage_activity_logs.contentName",
        "usage_activity_logs.score",
        "usage_activity_logs.userId",
        "usage_activity_logs.contentId",
        "usage_activity_logs.timeSpent",
        //"usage_activity_logs.attempt",
        "usage_activity_logs.isUserDeleted",
        "usage_activity_logs.teacherName",
        "usage_activity_logs.studentName",
        "usage_activity_logs.program",
        "usage_activity_logs.subject"
      ],
      measures: [
        "usage_activity_logs.name",
        "usage_activity_logs.test",
        "usage_activity_logs.externalStudentId"
      ],
      filters: [
        {
          member: "usage_activity_logs.activityType",
          operator: "notEquals",
          values: ["view"]
        }
      ]
    },
    "students-usage": {
      dimensions: [
        "usage_students.studentId",
        "usage_students.studentName",
        "usage_students.logins",
        "usage_students.pageViews",
        "usage_students.timeSpent",
        "usage_students.externalStudentId",
        "usage_students.grade"
      ],
      measures: [
        "usage_students.lessonsCompleted",
        "usage_students.questionsAnswered",
        "usage_students.submittedTests",
        "usage_students.subtestsCompleted"
      ]
    },
    "teachers-usage": {
      dimensions: ["usage_teachers.teacherId", "usage_teachers.teacherName"],
      measures: [
        "usage_teachers.logins",
        "usage_teachers.pageViews",
        "usage_teachers.timeSpent"
      ]
    },
    scores: SCORES.requestParams,
    "subtests-scores": SCORES_SUBTEST.requestParams,
    questions: QUESTIONS.requestParams,
    studentQuestions: STUDENT_QUESTIONS.requestParams,
    answers: ANSWERS.requestParams,
    goals: GOALS.requestParams,
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_TEST.requestParams,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.requestParams
  },
  "assignments-drawer": {
    content: {
      dimensions: [
        "content_view_ml_lessons.contentDisplayId",
        "content_view_ml_lessons.name",
        "content_view_ml_lessons.description"
      ]
    },
    students: {
      dimensions: [
        "org_view_students.studentId",
        "org_view_students.studentFullname"
      ]
    },
    classes: {
      dimensions: ["org_view_classes.classId", "org_view_classes.name"]
    }
    // segments: {
    //   dimensions: ["org_view_segments.segmentId", "org_view_segments.name"]
    // }
  }
}

export const REQUEST_FILTER_MAP: Record<
  string,
  Record<string, Record<string, string>>
> = {
  organization: {
    states: getOrgFiltersMap("org_view_states"),
    districts: getOrgFiltersMap("org_view_districts"),
    schools: getOrgFiltersMap("org_view_schools"),
    classes: getOrgFiltersMap("org_view_classes"),
    admins: getOrgFiltersMap("org_view_admins"),
    teachers: getOrgFiltersMap("org_view_teachers"),
    students: {
      ...getOrgFiltersMap("org_view_students"),
      "content.type": "org_view_students.contentType"
    },
    "license-pools": LICENSE_POOLS.requestFilterMap,
    "license-assign": LICENSE_ASSIGN.requestFilterMap
  },
  content: {
    "content-library": CONTENT_LIBRARY.requestFilterMap,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.requestFilterMap,
    "content-library/study-hall": CONTENT_LIBRARY_STUDY_HALL.requestFilterMap,
    "content-library/practice-test":
      CONTENT_LIBRARY_PRACTICE_TEST.requestFilterMap,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.requestFilterMap,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.requestFilterMap
  },
  insights: {
    "students-usage": {
      ...getOrgFiltersMap("usage_students"),
      "content.type": "usage_students.contentType",
      "tests.program": "usage_students.program",
      "tests.subject": "usage_students.subject"
      // "eventTypes.code": "usage_students.activityType"
    },
    "teachers-usage": {
      ...getOrgFiltersMap("usage_teachers"),
      "content.type": "usage_teachers.contentType"
      // "eventTypes.code": "usage_teachers.activityType"
    },
    usage: {
      ...getOrgFiltersMap("usage_activity_logs"),
      "students.id": "usage_activity_logs.userId",
      "teachers.id": "usage_activity_logs.userId",
      "eventTypes.code": "usage_activity_logs.activityType",
      "events.userRole": "usage_activity_logs.userRole",
      "content.type": "usage_activity_logs.contentType",
      "test_view_tests.name": "usage_activity_logs.contentName",
      "test_view_tests.id": "usage_activity_logs.contentId",
      "tests.program": "usage_activity_logs.program",
      "tests.subject": "usage_activity_logs.subject"
    },
    "student-activity-log": {
      "students.id": "usage_activity_logs.userId"
    },
    get "activity-log"() {
      return this.usage
    },
    get "details-usage"() {
      return this.usage
    },
    answers: ANSWERS.requestFilterMap,
    scores: SCORES.requestFilterMap,
    "subtests-scores": SCORES_SUBTEST.requestFilterMap,
    questions: QUESTIONS.requestFilterMap,
    studentQuestions: STUDENT_QUESTIONS.requestFilterMap,
    goals: GOALS.requestFilterMap,
    [GOALS_SUMMARY_BY_PROGRAM_SUBPAGE]:
      GOALS_SUMMARY_BY_PROGRAM.requestFilterMap,
    [GOALS_SUMMARY_BY_SUBJECT_SUBPAGE]:
      GOALS_SUMMARY_BY_SUBJECT.requestFilterMap,
    [SCORE_GOALS_BY_PROGRAM_VIEW_SUBPAGE]:
      SCORE_GOALS_BY_PROGRAM_VIEW.requestFilterMap,
    [SCORE_GOALS_BY_SUBJECT_VIEW_SUBPAGE]:
      SCORE_GOALS_BY_SUBJECT_VIEW.requestFilterMap,

    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_TEST.requestFilterMap,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.requestFilterMap
  },
  "assignments-drawer": {
    content: {
      "tests.program": "content_view_ml_lessons.program",
      "tests.subject": "content_view_ml_lessons.subject"
    }
  }
}

export const REQUEST_DEFAULT_ORDER = {
  organization: {
    states: { "org_view_states.code": "asc" },
    districts: { "org_view_districts.districtId": "asc" },
    schools: { "org_view_schools.id": "asc" },
    classes: { "org_view_classes.id": "asc" },
    admins: { "org_view_admins.id": "asc" },
    teachers: { "org_view_teachers.id": "asc" },
    students: { "org_view_students.studentId": "asc" },
    "license-pools": { "org_view_licenses.id": "asc" },
    "license-assign": { "org_view_students.studentFullname": "asc" }
  },
  content: {
    "content-library": CONTENT_LIBRARY.requestDefaultOrder,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.requestDefaultOrder,
    "content-library/study-hall":
      CONTENT_LIBRARY_STUDY_HALL.requestDefaultOrder,
    "content-library/practice-test":
      CONTENT_LIBRARY_PRACTICE_TEST.requestDefaultOrder,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.requestDefaultOrder,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.requestDefaultOrder
  },
  insights: {
    usage: { "usage_activity_logs.dateTime": "desc" },
    get "activity-log"() {
      return this.usage
    },
    get "details-usage"() {
      return this.usage
    },
    "students-usage": { "usage_students.studentId": "asc" },
    "teachers-usage": { "usage_teachers.teacherId": "asc" },
    questions: {
      "questions_view.displayName": "asc",
      "questions_view.seq": "asc",
      "questions_view.id": "asc"
    },
    studentQuestions: {
      "questions_view.displayName": "asc",
      "questions_view.seq": "asc",
      "questions_view.id": "asc"
    },
    "performance-report": {
      name: "asc"
    }
  },
  "teaching-tools": {
    assignments: { "assignments.name": "asc" },
    "assignment-record-students": {
      "assignment_record_students.studentName": "asc"
    }
  },
  "assignments-drawer": {
    content: { "content_view_ml_lessons.name": "asc" },
    students: { "org_view_students.studentFullname": "asc" },
    classes: { "org_view_classes.name": "asc" }
    // segments: { "org_view_segments.name": "asc" }
  }
}

export const ADDITIONAL_ORDER_REQUEST_FIELDS: Record<string, string[]> = {
  "content_view_list.program": ["content_view_list.subject"],
  "content_view_br_lessons.program": ["content_view_br_lessons.subject"],
  "content_view_sh_lessons.program": ["content_view_sh_lessons.subject"],
  "content_view_ml_lessons.program": ["content_view_ml_lessons.subject"],
  "content_view_mc_lessons.program": ["content_view_mc_lessons.subject"],
  "questions_view.displayName": ["questions_view.seq"],
  "questions_view.seq": ["questions_view.displayName"]
}

export const ALERT_PAGE_TITLE: Record<string, Record<string, string>> = {
  organization: {
    states: "States",
    districts: "Districts",
    schools: "Schools",
    classes: "Classes",
    admins: "Admins",
    teachers: "Teachers",
    students: "Students",
    "license-pools": "License Pools",
    "license-assign": "Licenses"
  },
  content: {
    "content-library": CONTENT_LIBRARY.alertPageTitle,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.alertPageTitle,
    "content-library/study-hall": CONTENT_LIBRARY_STUDY_HALL.alertPageTitle,
    "content-library/practice-test":
      CONTENT_LIBRARY_PRACTICE_TEST.alertPageTitle,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.alertPageTitle,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.alertPageTitle
  },
  insights: {
    usage: "Activity Log",
    get "details-usage"() {
      return this.usage
    },
    "students-usage": "Student Usage Summary",
    "teachers-usage": "Teacher Usage Summary",
    "activity-log": "Activity Log",
    scores: SCORES.alertPageTitle,
    "subtests-scores": SCORES_SUBTEST.alertPageTitle,
    questions: QUESTIONS.alertPageTitle,
    studentQuestions: STUDENT_QUESTIONS.alertPageTitle,
    goals: GOALS.alertPageTitle,
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_TEST.alertPageTitle,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.alertPageTitle
  }
}

export const SEARCH_REQUEST_PARAMS: Record<string, Record<string, string[]>> = {
  organization: {
    states: ["org_view_states.code"],
    districts: ["org_view_districts.districtName"],
    schools: ["org_view_schools.name"],
    classes: ["org_view_classes.name"],
    admins: ["org_view_admins.fullName", "org_view_admins.email"],
    teachers: ["org_view_teachers.fullName", "org_view_teachers.email"],
    students: [
      "org_view_students.studentFullname",
      "org_view_students.email",
      "org_view_students.externalStudentId"
    ],
    "license-pools": LICENSE_POOLS.searchRequestParams,
    "license-assign": LICENSE_ASSIGN.searchRequestParams
  },
  content: {
    "content-library": CONTENT_LIBRARY.searchRequestParams,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.searchRequestParams,
    "content-library/study-hall":
      CONTENT_LIBRARY_STUDY_HALL.searchRequestParams,
    "content-library/practice-test":
      CONTENT_LIBRARY_PRACTICE_TEST.searchRequestParams,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.searchRequestParams,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.searchRequestParams
  },
  insights: {
    "students-usage": [
      "usage_students.studentName",
      "usage_students.externalStudentId"
    ],
    "teachers-usage": ["usage_teachers.teacherName"],
    usage: [
      "usage_activity_logs.name",
      "usage_activity_logs.activityTypeDisplayName"
    ],
    "activity-log": [
      "usage_activity_logs.name",
      "usage_activity_logs.test",
      "usage_activity_logs.externalStudentId"
    ],
    get "details-usage"() {
      return [
        "usage_activity_logs.activityTypeDisplayName",
        "usage_activity_logs.externalStudentId"
      ]
    },
    scores: SCORES.searchRequestParams,
    "subtests-scores": SCORES_SUBTEST.searchRequestParams,
    questions: QUESTIONS.searchRequestParams,
    studentQuestions: STUDENT_QUESTIONS.searchRequestParams,
    answers: ANSWERS.searchRequestParams,
    goals: GOALS.searchRequestParams,
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_TEST.searchRequestParams,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.searchRequestParams
  },
  "assignments-drawer": {
    content: ["content_view_ml_lessons.name"],
    students: ["org_view_students.studentFullname"],
    classes: ["org_view_classes.name"]
    // segments: ["org_view_segments.name"]
  }
}

export const ADDITIONAL_FILTERS_BY_FEATURE_FLAG: Record<
  string,
  Record<string, Record<string, any>>
> = {
  content: {
    "content-library": {
      [FeatureFlagName.reporting_content_library_skill_review_mastery_curriculum]:
        [
          {
            member: "content_view_list.type",
            operator: "equals",
            values: [
              ContentType.bellRinger,
              ContentType.masteryLesson,
              ContentType.practiceTest
            ]
          }
        ]
    }
  }
}

export const EXCLUDED_FILTERS_BY_ROLE: Record<
  string,
  Record<string, Record<string, string[]>>
> = {
  organization: {
    states: {
      [Roles.Super_Administrator]: ["test"]
    },
    districts: {
      [Roles.Super_Administrator]: ["test"]
    },
    admins: {
      [Roles.Super_Administrator]: ["test"],
      [Roles.District_Admin]: ["test"],
      [Roles.School_Admin]: ["test"]
    },
    schools: {
      [Roles.Super_Administrator]: ["test"],
      [Roles.District_Admin]: ["test"]
    },
    teachers: {
      [Roles.Super_Administrator]: ["test"],
      [Roles.District_Admin]: ["test"],
      [Roles.School_Admin]: ["test"]
    },
    classes: {
      [Roles.Super_Administrator]: ["test"],
      [Roles.District_Admin]: ["test"],
      [Roles.School_Admin]: ["test"],
      [Roles.Teacher]: ["test"]
    },
    students: {
      [Roles.Super_Administrator]: ["test"],
      [Roles.District_Admin]: ["test"],
      [Roles.School_Admin]: ["test"],
      [Roles.Teacher]: ["test"]
    }
  },
  insights: {
    usage: {
      [Roles.Teacher]: ["teacher"]
    },
    "activity-log": {
      [Roles.Teacher]: ["teacher"]
    },
    get "details-usage"() {
      return this.usage
    }
  }
}

export const TABLE_OPTIONS: Record<string, Record<string, TableOptions>> = {
  organization: {
    states: {
      entityLabel: "State",
      caption: "List of states"
    },
    districts: {
      entityLabel: "District",
      caption: "List of districts"
    },
    admins: {
      entityLabel: "Admin",
      caption: "List of admins"
    },
    schools: {
      entityLabel: "School",
      caption: "List of schools"
    },
    teachers: {
      entityLabel: "Teacher",
      caption: "List of teachers"
    },
    classes: {
      entityLabel: "Class",
      caption: "List of classes"
    },
    students: {
      entityLabel: "Student",
      caption: "List of students"
    },
    "license-pools": LICENSE_POOLS.tableOptions,
    "license-assign": LICENSE_ASSIGN.tableOptions
  },
  content: {
    "content-library": CONTENT_LIBRARY.tableOptions,
    "content-library/element": CONTENT_LIBRARY_ELEMENT.tableOptions,
    "content-library/study-hall": CONTENT_LIBRARY_STUDY_HALL.tableOptions,
    "content-library/practice-test": CONTENT_LIBRARY_PRACTICE_TEST.tableOptions,
    "content-library/mastery-lesson":
      CONTENT_LIBRARY_MASTERY_LESSON.tableOptions,
    "content-library/mastery-curriculum":
      CONTENT_LIBRARY_MASTERY_CURRICULUM.tableOptions
  },
  insights: {
    usage: {
      searchPlaceholder: "Activities",
      pageName: "Activity Log",
      selectable: false,
      caption: "List of activity log entries"
    },
    "activity-log": {
      searchPlaceholder: "Activities",
      pageName: "Activity Log",
      selectable: false,
      caption: "List of activity log entries"
    },
    get "details-usage"() {
      return this.usage
    },
    "students-usage": {
      searchPlaceholder: "Usage",
      selectable: false,
      pageName: "Student Usage",
      caption: "List of student usage records"
    },
    "teachers-usage": {
      searchPlaceholder: "Usage",
      selectable: false,
      pageName: "Teacher Usage",
      caption: "List of teacher usage records"
    },
    performance: {
      pageName: "Performance"
    },
    scores: SCORES.tableOptions,
    "subtests-scores": SCORES_SUBTEST.tableOptions,
    questions: QUESTIONS.tableOptions,
    studentQuestions: STUDENT_QUESTIONS.tableOptions,
    answers: ANSWERS.tableOptions,
    goals: GOALS.tableOptions,
    "content-mastery": {
      pageName: "Content Mastery"
    },
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]: GOALS_SCORE_RANGE_BY_TEST.tableOptions,
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]:
      GOALS_SCORE_RANGE_BY_SUBJECT.tableOptions,
    "custom-reports": {
      searchPlaceholder: "Custom Reports",
      entityLabel: "Custom Report",
      selectable: false
    },
    "custom-reports-view": {
      searchPlaceholder: "Custom Reports",
      entityLabel: "Custom Report",
      selectable: false
    },
    "performance-report": {
      searchPlaceholder: "Report",
      selectable: false,
      hideColumnSelector: true
    }
  },
  "teaching-tools": {
    "bell-ringers": {
      searchPlaceholder: "Bell Ringers"
    },
    "study-plan": {
      pageName: "Study Plan"
    },
    assignments: {
      selectable: false,
      entityLabel: "Assignment"
    },
    "assignment-record-students": {
      selectable: false,
      entityLabel: "Student",
      searchPlaceholder: "Students"
    }
  }
}

export const CONTENT_VIEWS_BY_TYPE_MAP = {
  content_view_list: "mixed",
  content_view_br_lessons: ContentType.bellRinger,
  content_view_sh_lessons: ContentType.skillReview,
  content_view_tests: ContentType.practiceTest,
  content_view_ml_lessons: ContentType.masteryLesson,
  content_view_mc_lessons: ContentType.masteryCurriculum
}

// Dimensions that should always be included in Cube requests,
// even if the corresponding column is hidden in the UI.
export const ALWAYS_INCLUDED_DIMENSIONS_TO_CUBE_REQUEST = {
  content: {
    "content-library/practice-test": [
      "content_view_tests.name" // Needed for diagnostic score formatting (Average score column)
    ]
  }
}
